import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { api } from '../../lib/api';
import { ASSETS, BRAND_COLORS, FONT_STACKS } from '../../lib/brand';
import { useAuthStore } from '../../store/authStore';
import type { CompanyAsset } from '../../types/slide';
import { ConfirmDialog } from '../ConfirmDialog';
import { EmptyState, PageHeader, Section } from './portalParts';

const TEMPLATE_PPTX = '/template/AST-Presentation-Template.pptx';

function AssetCard({ preview, title, detail, href, fileName }: { preview: ReactNode; title: string; detail: string; href: string; fileName: string }) {
  return (
    <Box sx={{ borderRadius: 2.5, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ aspectRatio: '16 / 9', borderBottom: 1, borderColor: 'divider', overflow: 'hidden' }}>{preview}</Box>
      <Stack direction="row" spacing={1} sx={{ p: 1.5, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="caption" noWrap component="div">
            {detail}
          </Typography>
        </Box>
        <Tooltip title={`Download ${fileName}`}>
          <IconButton component="a" href={href} download={fileName} aria-label={`Download ${title}`}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function ColorSwatch({ name, hex }: { name: string; hex: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ borderRadius: 2.5, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ height: 72, bgcolor: hex, borderBottom: 1, borderColor: 'divider' }} />
      <Stack direction="row" sx={{ p: 1.25, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
            {hex}
          </Typography>
        </Box>
        <Tooltip title={copied ? 'Copied' : 'Copy hex'}>
          <IconButton
            size="small"
            aria-label={`Copy ${name} ${hex}`}
            onClick={() => void navigator.clipboard.writeText(hex).then(() => setCopied(true))}
          >
            {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

const FONTS = [
  { name: 'Lora', role: 'Cover and closing titles', stack: FONT_STACKS.display, href: 'https://fonts.google.com/specimen/Lora', sample: 'Weekly Leadership Update' },
  { name: 'Arial', role: 'Slide headers and body text', stack: FONT_STACKS.body, href: null, sample: 'Key performance indicators' },
  { name: 'Poppins', role: 'Subtitles, labels and footer', stack: FONT_STACKS.label, href: 'https://fonts.google.com/specimen/Poppins', sample: 'ADVANCED SUBSTRATE TECHNOLOGIES' },
];

function MediaLibrary() {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const [assets, setAssets] = useState<CompanyAsset[] | null>(null);
  const [storageEnabled, setStorageEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<CompanyAsset | null>(null);

  const load = useCallback(
    () =>
      api
        .listAssets()
        .then((r) => {
          setAssets(r.assets);
          setStorageEnabled(r.storageEnabled);
        })
        .catch((err) => {
          setAssets([]);
          setError(err instanceof Error ? err.message : 'Could not load the media library');
        }),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async (files: FileList) => {
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) await api.uploadAsset(file, 'photos');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Media library"
      icon={<ImageOutlinedIcon />}
      action={
        isAdmin && (
          <Button
            component="label"
            size="small"
            variant="outlined"
            color="neutral"
            disabled={busy || !storageEnabled}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CloudUploadOutlinedIcon />}
          >
            Upload images
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              multiple
              onChange={(e) => e.target.files?.length && void upload(e.target.files)}
            />
          </Button>
        )
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
        Approved photos and graphics. They also appear under Media in the editor.
      </Typography>
      {!storageEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isAdmin
            ? 'Uploads are off until file storage is enabled: turn on R2 in the Cloudflare dashboard, then ask for the bucket to be connected.'
            : 'The shared media library isn’t set up yet.'}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {assets === null ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" sx={{ aspectRatio: '4 / 3', height: 'auto' }} />
          ))}
        </Box>
      ) : assets.length ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
          {assets.map((a) => (
            <Box key={a.id} sx={{ borderRadius: 2.5, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Box component="img" src={a.r2_url} alt={a.name} loading="lazy" sx={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }} />
              <Stack direction="row" sx={{ alignItems: 'center', p: 1, pl: 1.5 }}>
                <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {a.name}
                </Typography>
                <Tooltip title="Download">
                  <IconButton size="small" component="a" href={a.r2_url} download={a.name} aria-label={`Download ${a.name}`}>
                    <FileDownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isAdmin && (
                  <Tooltip title="Delete">
                    <IconButton size="small" aria-label={`Delete ${a.name}`} onClick={() => setToDelete(a)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      ) : (
        <EmptyState>No images in the library yet.</EmptyState>
      )}
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete image?"
        message={`“${toDelete?.name ?? ''}” will be removed from the library. Slides that already use it keep their copy only if it was embedded.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const a = toDelete;
          if (a) void api.deleteAsset(a.id).then(load).catch((err) => setError(String(err)));
        }}
      />
    </Section>
  );
}

export function AssetsPage() {
  return (
    <>
      <PageHeader
        title="Company assets"
        description="The AST brand kit and approved media. Use these in documents, slides and posters."
      />

      <Section title="Logos and template" icon={<FileDownloadOutlinedIcon />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
          <AssetCard
            title="AST logo"
            detail="PNG · for light backgrounds"
            href={ASSETS.logo}
            fileName="AST-logo.png"
            preview={
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', bgcolor: '#FFFFFF' }}>
                <Box component="img" src={ASSETS.logo} alt="" sx={{ width: '58%' }} />
              </Box>
            }
          />
          <AssetCard
            title="AST logo, white"
            detail="PNG · for dark or photo backgrounds"
            href={ASSETS.logoWhite}
            fileName="AST-logo-white.png"
            preview={
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', bgcolor: 'primary.main' }}>
                <Box component="img" src={ASSETS.logoWhite} alt="" sx={{ width: '58%' }} />
              </Box>
            }
          />
          <AssetCard
            title="PowerPoint template"
            detail="PPTX · cover, content and thank-you layouts"
            href={TEMPLATE_PPTX}
            fileName="AST Presentation Template.pptx"
            preview={
              <Box
                sx={{
                  height: '100%',
                  backgroundImage: `url(${ASSETS.coverBackground})`,
                  backgroundSize: 'cover',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box component="img" src={ASSETS.logoWhite} alt="" sx={{ width: '28%' }} />
                <Typography sx={{ color: '#FFFFFF', fontFamily: FONT_STACKS.display, fontWeight: 700, fontSize: 20 }}>
                  Presentation title
                </Typography>
              </Box>
            }
          />
          <AssetCard
            title="Cover background"
            detail="JPG · 1920 × 1080"
            href={ASSETS.coverBackground}
            fileName="AST-cover-background.jpg"
            preview={<Box component="img" src={ASSETS.coverBackground} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          />
        </Box>
      </Section>

      <Section title="Brand colours" icon={<PaletteOutlinedIcon />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
          {BRAND_COLORS.map((c) => (
            <ColorSwatch key={c.hex} name={c.name} hex={c.hex} />
          ))}
        </Box>
      </Section>

      <Section title="Typography" icon={<TextFieldsIcon />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
          {FONTS.map((f) => (
            <Box key={f.name} sx={{ p: 2, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Typography sx={{ fontFamily: f.stack, fontSize: 24, fontWeight: 700, lineHeight: 1.2, mb: 1, color: 'primary.main' }}>
                {f.sample}
              </Typography>
              <Stack direction="row" sx={{ alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {f.name}
                  </Typography>
                  <Typography variant="caption">{f.role}</Typography>
                </Box>
                {f.href ? (
                  <Button size="small" color="neutral" component="a" href={f.href} target="_blank" rel="noopener noreferrer">
                    Get font
                  </Button>
                ) : (
                  <Typography variant="caption">Built into Windows and macOS</Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      </Section>

      <MediaLibrary />
    </>
  );
}
